import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "../Firebase";

const JAN_2025 = new Date("2025-01-01T00:00:00");

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);
const cv = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));

const getQuarter = (d) => Math.floor(d.getMonth() / 3) + 1;
const getYM = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const monthLabel = (ym) => {
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1, 1)
        .toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

const quarterLabel = (yq) => {
    const [y, q] = yq.split("-Q");
    return `Q${q} ${y}`;
};

export default function WorkshopDataDateWise() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [monthFilter, setMonthFilter] = useState("all");
    const [quarterFilter, setQuarterFilter] = useState("all");
    const [modal, setModal] = useState(null);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const today = new Date();

                // 1. Get all funder_users_list phones to exclude (doc IDs are phone numbers)
                const funderSnap = await getDocs(collection(db, "funder_users_list"));
                const excludedPhones = new Set();
                funderSnap.forEach(d => {
                    const byId = compactPhone(d.id);
                    if (byId) excludedPhones.add(byId);
                    const byField = compactPhone(d.data().phone_no);
                    if (byField) excludedPhones.add(byField);
                });

                // 2. Fetch workshop_registration, filter Jan 2025 – today
                const wsSnap = await getDocs(collection(db, "workshop_registration"));
                const phoneMap = {};

                wsSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const dt = parseDate(d.created_date);
                    if (!dt || dt < JAN_2025 || dt > today) return;

                    const phone = compactPhone(d.phone);
                    if (!phone || phone.length < 10) return;
                    if (excludedPhones.has(phone)) return;

                    if (!phoneMap[phone]) {
                        phoneMap[phone] = { phone, name: cv(d.full_name), workshops: [] };
                    }
                    if (phoneMap[phone].name === "-" && d.full_name) {
                        phoneMap[phone].name = cv(d.full_name);
                    }

                    phoneMap[phone].workshops.push({
                        eventName: cv(d.workshop_event_name),
                        description: cv(d.description),
                        displayDate: d.workshop_Date || d.created_date,
                        parsedDate: dt,          // always created_date — guaranteed >= Jan 2025
                        location: cv(d.workshop_location),
                        payment: cv(d.payment),
                    });
                });

                const result = Object.values(phoneMap).map(r => {
                    r.workshops.sort((a, b) =>
                        (b.parsedDate?.getTime() || 0) - (a.parsedDate?.getTime() || 0)
                    );
                    return r;
                });
                result.sort((a, b) => b.workshops.length - a.workshops.length);

                setRows(result);
            } catch (err) {
                console.error("WorkshopDataDateWise fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    // Unique months + quarters derived from all workshops
    const { months, quarters } = useMemo(() => {
        const mSet = new Set();
        const qSet = new Set();
        rows.forEach(r => r.workshops.forEach(w => {
            if (!w.parsedDate) return;
            mSet.add(getYM(w.parsedDate));
            qSet.add(`${w.parsedDate.getFullYear()}-Q${getQuarter(w.parsedDate)}`);
        }));
        return {
            months: ["all", ...Array.from(mSet).sort().reverse()],
            quarters: ["all", ...Array.from(qSet).sort().reverse()],
        };
    }, [rows]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows
            .map(r => {
                let ws = r.workshops;
                if (monthFilter !== "all") {
                    ws = ws.filter(w => w.parsedDate && getYM(w.parsedDate) === monthFilter);
                }
                if (quarterFilter !== "all") {
                    const [yr, qn] = quarterFilter.split("-Q");
                    ws = ws.filter(w => w.parsedDate &&
                        w.parsedDate.getFullYear() === Number(yr) &&
                        getQuarter(w.parsedDate) === Number(qn)
                    );
                }
                return { ...r, wsFiltered: ws };
            })
            .filter(r => {
                if (r.wsFiltered.length === 0) return false;
                if (!q) return true;
                return r.phone.includes(q) || r.name.toLowerCase().includes(q);
            })
            .sort((a, b) => b.wsFiltered.length - a.wsFiltered.length);
    }, [rows, search, monthFilter, quarterFilter]);

    const totalAttendances = filtered.reduce((s, r) => s + r.wsFiltered.length, 0);

    const handleExport = () => {
        const summary = filtered.map((r, i) => ({
            "#": i + 1,
            "Name": r.name,
            "Phone": r.phone,
            "Workshops Attended": r.wsFiltered.length,
        }));
        const details = [];
        filtered.forEach(r => r.wsFiltered.forEach((w, wi) => {
            details.push({
                "Phone": r.phone, "Name": r.name, "#": wi + 1,
                "Workshop Name": w.eventName, "Description": w.description,
                "Workshop Date": fmtDate(w.displayDate), "Location": w.location, "Payment": w.payment,
            });
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), "Summary");
        if (details.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(details), "Workshop Details");
        XLSX.writeFile(wb, "workshop_data_datewise.xlsx");
    };

    const isFiltering = monthFilter !== "all" || quarterFilter !== "all";

    return (
        <>
            <div className="container-fluid py-4">

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h3 className="mb-1">Workshop Attendance (Non U+ members)</h3>
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                            workshop_registration · Jan 2025 – Today · Funder users excluded
                        </div>
                    </div>
                    <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
                </div>

                {/* SUMMARY CARDS */}
                {/* {!loading && (
                    <div className="row g-3 mb-3">
                        <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                                <div style={{ fontSize: "2rem", fontWeight: 800, color: "#4361ee" }}>{filtered.length}</div>
                                <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                                    Unique Users{isFiltering ? " (filtered)" : ""}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                                <div style={{ fontSize: "2rem", fontWeight: 800, color: "#0d9488" }}>{totalAttendances}</div>
                                <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                                    Total Attendances{isFiltering ? " (filtered)" : ""}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                                <div style={{ fontSize: "2rem", fontWeight: 800, color: "#7c3aed" }}>{rows.length}</div>
                                <div style={{ fontSize: "12px", color: "#94a3b8" }}>Total Unique Users</div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px" }}>
                                <div style={{ fontSize: "2rem", fontWeight: 800, color: "#d97706" }}>
                                    {filtered.length > 0 ? (totalAttendances / filtered.length).toFixed(1) : "0"}
                                </div>
                                <div style={{ fontSize: "12px", color: "#94a3b8" }}>Avg Workshops / User</div>
                            </div>
                        </div>
                    </div>
                )} */}

                {/* FILTERS */}
                <div className="card mb-3">
                    <div className="card-body py-3 px-4">
                        <div className="d-flex gap-3 align-items-center flex-wrap">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name or phone…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ maxWidth: "260px" }}
                            />

                            {/* Monthly slicer */}
                            <select
                                className="form-select"
                                value={monthFilter}
                                onChange={e => { setMonthFilter(e.target.value); setQuarterFilter("all"); }}
                                style={{ maxWidth: "160px" }}
                            >
                                <option value="all">All Months</option>
                                {months.filter(m => m !== "all").map(m => (
                                    <option key={m} value={m}>{monthLabel(m)}</option>
                                ))}
                            </select>

                            {/* Quarterly slicer */}
                            <select
                                className="form-select"
                                value={quarterFilter}
                                onChange={e => { setQuarterFilter(e.target.value); setMonthFilter("all"); }}
                                style={{ maxWidth: "160px" }}
                            >
                                <option value="all">All Quarters</option>
                                {quarters.filter(q => q !== "all").map(q => (
                                    <option key={q} value={q}>{quarterLabel(q)}</option>
                                ))}
                            </select>

                            {isFiltering && (
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => { setMonthFilter("all"); setQuarterFilter("all"); }}
                                >
                                    Clear Filters
                                </button>
                            )}

                            {(search || isFiltering) && (
                                <span className="text-muted" style={{ fontSize: "13px" }}>
                                    {filtered.length} user{filtered.length !== 1 ? "s" : ""}
                                </span>
                            )}

                            <button
                                className="btn btn-success ms-auto"
                                onClick={handleExport}
                                disabled={loading || filtered.length === 0}
                                style={{ minWidth: "140px", fontWeight: 700 }}
                            >
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                        <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                    </div>
                ) : (
                    <div className="card">
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Phone No</th>
                                        <th className="text-center">Workshops Attended</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4 text-muted">
                                                No data found
                                            </td>
                                        </tr>
                                    ) : filtered.map((row, i) => (
                                        <tr key={row.phone}>
                                            <td>{i + 1}</td>
                                            <td style={{ fontWeight: 600 }}>{row.name}</td>
                                            <td>{row.phone}</td>
                                            <td className="text-center">
                                                <span
                                                    className="badge bg-primary"
                                                    style={{ cursor: "pointer", fontSize: "13px", padding: "5px 12px" }}
                                                    onClick={() => setModal(row)}
                                                    title="Click to see workshops"
                                                >
                                                    {row.wsFiltered.length}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* WORKSHOP DETAIL MODAL */}
            {modal && (
                <div
                    onClick={() => setModal(null)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                        zIndex: 9999, display: "flex", alignItems: "center",
                        justifyContent: "center", padding: "20px",
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#fff", borderRadius: "8px", width: "100%",
                            maxWidth: "700px", maxHeight: "80vh",
                            display: "flex", flexDirection: "column",
                            boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                            <div>
                                <div className="fw-bold" style={{ fontSize: "15px" }}>
                                    Workshops ({modal.wsFiltered.length})
                                </div>
                                <div className="text-muted" style={{ fontSize: "12px" }}>
                                    {modal.name} · {modal.phone}
                                </div>
                            </div>
                            <button className="btn-close" onClick={() => setModal(null)} />
                        </div>
                        <div style={{ overflowY: "auto", padding: "16px" }}>
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Workshop Name</th>
                                        <th>Workshop Date</th>
                                        <th>Location</th>
                                        <th>Payment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modal.wsFiltered.map((w, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{w.eventName}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>{fmtDate(w.displayDate)}</td>
                                            <td>{w.location}</td>
                                            <td>{w.payment}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
